import { createSelector } from 'reselect';

import type { BillableInvoiceItemType } from 'app-components/flux/billable-invoice-items/billableInvoiceItemsTypes';

import { billableInvoiceItemsSelectors as stdSliceSelectors } from './billableInvoiceItemsSlice';

export { stdSliceSelectors };

const getBillableInvoiceItemTotal = ({ rate, units }: BillableInvoiceItemType): number => {
  return (rate ?? 0) * (units ?? 0);
};

// currently not used - we would need to add this to the store and the sagas
// export const getLastSyncDate = (state: Pick<RootState, BILLABLE_INVOICE_ITEMS_REDUCER_KEY>) => getBillableInvoiceItemsStore(state).lastSyncDate;

// Get map of items by items project-id. If item doesn't belong to a project, then those items will be
// mapped to the customerId.
//
export const getBillableInvoiceItemsByProjectId = createSelector(
  stdSliceSelectors.selectAll,
  (billableInvoiceItems: BillableInvoiceItemType[]) =>
    billableInvoiceItems.reduce((acc, item) => {
      const id = item.projectId && item.projectId !== 'no-project' ? item.projectId : item.customerId;
      if (!acc.has(id)) {
        acc.set(id, []); // Initialize array if key doesn't exist
      }
      acc.get(id)?.push(item);
      return acc;
    }, new Map<string, BillableInvoiceItemType[]>()),
);

// Get map of items for project-id. You can just use the customer-id with no project-id to get
// non-project items.
//
export const getBillableInvoiceItemsForProjectId = createSelector(
  getBillableInvoiceItemsByProjectId,
  (_state: any, props: { projectId?: string | null; customerId?: string | null }) => props.projectId,
  (_state: any, props: { projectId?: string | null; customerId?: string | null }) => props.customerId,
  (billableInvoiceItems: Map<string, BillableInvoiceItemType[]>, projectId, customerId) => {
    const id = projectId && projectId !== 'no-project' ? projectId : customerId;
    if (!id) return [];

    return billableInvoiceItems.get(id) ?? [];
  },
);

// Get billable sum for project-id. You can just use the customer-id with no project-id to get
// non-project billable summary.
//
export const getBillableInvoiceSumForProjectId = createSelector(
  getBillableInvoiceItemsForProjectId,
  (billableInvoiceItems: BillableInvoiceItemType[]) => {
    return billableInvoiceItems.reduce(
      (sum: number, item: BillableInvoiceItemType) => sum + getBillableInvoiceItemTotal(item),
      0,
    );
  },
);

// Get map of items by customer-id. This will return all items whether they belong to a project or not.
//
export const getBillableInvoiceItemsByCustomerId = createSelector(
  stdSliceSelectors.selectAll,
  (billableInvoiceItems: BillableInvoiceItemType[]) =>
    billableInvoiceItems.reduce((acc, item) => {
      const id = item.customerId;
      if (!id) return acc;

      if (!acc.has(id)) {
        acc.set(id, []); // Initialize array if key doesn't exist
      }
      acc.get(id)?.push(item);
      return acc;
    }, new Map<string, BillableInvoiceItemType[]>()),
);

// Get items for customer-id. This will return all items whether they belong to a project or not.
//
export const getBillableInvoiceItemsForCustomerId = createSelector(
  getBillableInvoiceItemsByCustomerId,
  (_state: any, props: { customerId?: string | null }) => props.customerId,
  (billableInvoiceItems: Map<string, BillableInvoiceItemType[]>, customerId) => {
    if (!customerId) return [];

    return billableInvoiceItems.get(customerId) ?? [];
  },
);

export const getBillableInvoiceSumForCustomerId = createSelector(
  getBillableInvoiceItemsForCustomerId,
  (billableInvoiceItems: BillableInvoiceItemType[]) => {
    return billableInvoiceItems.reduce(
      (sum: number, item: BillableInvoiceItemType) => sum + getBillableInvoiceItemTotal(item),
      0,
    );
  },
);
